import { pb } from './pocketbase';
import { createNotification } from './notification.service';

/**
 * Gère de manière centralisée les erreurs pour chaque fonction.
 * @param {Error} error L'objet d'erreur.
 * @param {string} message Le message d'erreur personnalisé.
 * @returns {{success: boolean, error: string}}
 */
const handleServiceError = (error, message) => {
    console.error(message, error);
    return {
        success: false,
        error: error.message || message,
    };
};

/**
 * Vérifie si l'utilisateur est authentifié et a un partner_id.
 * @returns {object} Le modèle d'utilisateur PocketBase.
 * @throws {Error} Si l'utilisateur n'est pas authentifié ou manque de partner_id.
 */
const getAuthenticatedUser = () => {
    const user = pb.authStore.model;
    if (!user || !user.partner_id) {
        throw new Error('Utilisateur non authentifié ou partner_id manquant.');
    }
    return user;
};

// Créer une offre d'alternance
const createOffer = async (offerData) => {
    try {
        const user = getAuthenticatedUser();
        const company = await pb.collection('companies').getFirstListItem(`partner_id = "${user.partner_id}"`);
        
        const { job_description, ...otherData } = offerData;
        
        const offerToCreate = {
            ...otherData,
            partner_id: user.partner_id,
            company_name: company.name, 
            status: 'Active',
        };

        if (job_description instanceof File) {
            offerToCreate.job_description = job_description;
        }
        
        const record = await pb.collection('alternance_offers').create(offerToCreate);

        // La création de la notification ne doit pas bloquer le processus
        try {
            await createNotification({
                type: "offer_created",
                title: "Création d'une offre",
                message: `L'entreprise ${company.name} a créé une nouvelle offre d'alternance : "${record.title}"`,
                company_id: company.id,
                sender_type: "company",
                icon: "convention_logo.png",
                link: `/admin/companies/${company.id}`,
                read: false,
            });
        } catch (notificationError) {
            console.error("Erreur lors de la création de la notification pour l'offre:", notificationError);
        }

        return { success: true, data: record };
    } catch (error) {
        return handleServiceError(error, 'Erreur lors de la création de l\'offre.');
    }
};

// Récupérer toutes les offres
const getOffers = async () => {
    try {
        const records = await pb.collection('alternance_offers').getFullList({
            sort: '-created',
            expand: 'company_id,partner_id',
        });
        return { success: true, data: records };
    } catch (error) {
        return handleServiceError(error, 'Erreur lors de la récupération des offres.');
    }
};

// Récupérer les offres d'une entreprise spécifique
const getCompanyOffers = async (options = {}) => {
    try {
        const user = getAuthenticatedUser();
        const {
            page = 1,
            perPage = 10,
            sort = '-created',
            search = '',
            status = '',
        } = options;

        let filter = `partner_id = "${user.partner_id}"`;
        
        if (search.trim()) {
            filter += ` && (title ~ "${search}" || description ~ "${search}" || job_location ~ "${search}")`;
        }
        
        if (status.trim()) {
            filter += ` && status = "${status}"`;
        }

        const records = await pb.collection('alternance_offers').getList(page, perPage, {
            sort,
            filter,
            expand: 'company_id,candidats_ids',
        });

        return { 
            success: true, 
            data: records.items,
            totalPages: records.totalPages,
            totalItems: records.totalItems,
            page: records.page,
            perPage: records.perPage,
        };
    } catch (error) {
        return handleServiceError(error, 'Erreur lors de la récupération des offres de l\'entreprise.');
    }
};

// Récupérer une offre par ID
const getOfferById = async (id) => {
    try {
        const record = await pb.collection('alternance_offers').getOne(id, {
            expand: 'company_id,candidats_ids',
        });
        return { success: true, data: record };
    } catch (error) {
        if (error.status === 404) {
            return { success: false, error: 'Offre d\'alternance non trouvée.' };
        }
        return handleServiceError(error, 'Erreur lors de la récupération de l\'offre.');
    }
};

// Mettre à jour une offre
const updateOffer = async (id, offerData) => {
    try {
        const record = await pb.collection('alternance_offers').update(id, {
            ...offerData,
            updated: new Date().toISOString(),
        });
        return { success: true, data: record };
    } catch (error) {
        return handleServiceError(error, 'Erreur lors de la mise à jour de l\'offre.');
    }
};

// Supprimer une offre
const deleteOffer = async (id) => {
    try {
        await pb.collection('alternance_offers').delete(id);
        return { success: true };
    } catch (error) {
        return handleServiceError(error, 'Erreur lors de la suppression de l\'offre.');
    }
};

// Supprimer plusieurs offres en lot
const deleteMultipleOffers = async (offerIds) => {
    try {
        await Promise.all(offerIds.map(id => pb.collection('alternance_offers').delete(id)));
        return { success: true, deletedCount: offerIds.length };
    } catch (error) {
        return handleServiceError(error, 'Erreur lors de la suppression des offres.');
    }
};

const getOfferCandidates = async (offerId) => {
    try {
        const offer = await pb.collection('alternance_offers').getOne(offerId);

        if (!offer.candidats_ids || offer.candidats_ids.length === 0) {
            return { success: true, data: [] };
        }

        const candidates = await Promise.all(
            offer.candidats_ids.map(id =>
                pb.collection('students').getOne(id, { expand: 'formation,company' })
                    .catch(err => {
                        console.error(`Erreur lors de la récupération du candidat ${id}:`, err);
                        return null;
                    })
            )
        );

        const validCandidates = candidates.filter(c => c !== null);

        return { success: true, data: validCandidates };
    } catch (error) {
        return handleServiceError(error, "Erreur lors de la récupération des candidats de l'offre.");
    }
};

export {
    createOffer,
    getOffers,
    getCompanyOffers,
    getOfferById,
    updateOffer,
    deleteOffer,
    deleteMultipleOffers,
    getOfferCandidates,
};